import { useCallback, useEffect, useState } from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { ThumbsUp, Warning, Plus } from "@phosphor-icons/react";
import { Spinner } from "@heroui/react";
import StarRating from "./StarRating";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  type CandidateTag,
  CandidateTagCategory,
  POSITIVE_TAG_LABELS,
  CONCERN_TAG_LABELS,
  PositiveTagKey,
  ConcernTagKey,
  RATING_LABELS,
} from "shared/types/candidate-follow-up";
import * as Sentry from "@sentry/react";

interface AssessmentTabProps {
  applicationId: number;
  initialRating: number | null;
  onRatingChange?: (rating: number | null) => void;
}

const ALL_POSITIVE_TAGS = Object.entries(POSITIVE_TAG_LABELS).map(
  ([key, label]) => ({ key, label })
);
const ALL_CONCERN_TAGS = Object.entries(CONCERN_TAG_LABELS).map(
  ([key, label]) => ({ key, label })
);

const AssessmentTab = ({
  applicationId,
  initialRating,
  onRatingChange,
}: AssessmentTabProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [rating, setRating] = useState<number | null>(initialRating);
  const [tags, setTags] = useState<CandidateTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingRating, setIsSavingRating] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [positiveCustomText, setPositiveCustomText] = useState("");
  const [concernCustomText, setConcernCustomText] = useState("");

  // Fetch tags on mount
  useEffect(() => {
    fetchTags();
  }, [applicationId]);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/follow-up/tags/${applicationId}`,
        { method: "GET" },
        session
      );

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      setTags(data);

      // Restore custom text from existing "other" tags
      const existingPositiveOther = data.find(
        (t: CandidateTag) =>
          t.category === CandidateTagCategory.POSITIVE &&
          t.tagKey === PositiveTagKey.OTHER
      );
      const existingConcernOther = data.find(
        (t: CandidateTag) =>
          t.category === CandidateTagCategory.CONCERN &&
          t.tagKey === ConcernTagKey.OTHER
      );
      if (existingPositiveOther?.customText)
        setPositiveCustomText(existingPositiveOther.customText);
      if (existingConcernOther?.customText)
        setConcernCustomText(existingConcernOther.customText);
    } catch (error) {
      console.error("Error fetching tags:", error);
      Sentry.captureException(error, {
        tags: { feature: "candidateFollowUp", method: "fetchTags" },
        extra: { applicationId },
      });
      toast.error("Fout bij het ophalen van tags");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRatingChange = useCallback(
    async (newRating: number | null) => {
      setRating(newRating);
      setIsSavingRating(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/follow-up/rating/${applicationId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rating: newRating }),
          },
          session
        );

        if (!response.ok) throw new Error("Failed to update rating");
        onRatingChange?.(newRating);
      } catch (error) {
        console.error("Error updating rating:", error);
        Sentry.captureException(error, {
          tags: { feature: "candidateFollowUp", method: "handleRatingChange" },
          extra: { applicationId, newRating },
        });
        toast.error("Fout bij het bijwerken van de score");
        setRating(initialRating);
      } finally {
        setIsSavingRating(false);
      }
    },
    [applicationId, session, initialRating, onRatingChange]
  );

  const isTagSelected = (category: CandidateTagCategory, tagKey: string) => {
    return tags.some((t) => t.category === category && t.tagKey === tagKey);
  };

  const saveTags = useCallback(async (
    newTags: { category: CandidateTagCategory; tagKey: string; customText?: string }[]
  ) => {
    setIsSavingTags(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/follow-up/tags`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            applicationId,
            tags: newTags,
            createdByUserId: user?.id,
          }),
        },
        session
      );

      if (!response.ok) throw new Error("Failed to save tags");

      const savedTags = await response.json();
      setTags(savedTags);
    } catch (error) {
      console.error("Error saving tags:", error);
      Sentry.captureException(error, {
        tags: { feature: "candidateFollowUp", method: "saveTags" },
        extra: { applicationId },
      });
      toast.error("Fout bij het opslaan van tags");
    } finally {
      setIsSavingTags(false);
    }
  }, [applicationId, session, user?.id]);

  // Memoized so the 15+ tag buttons don't receive a new function reference on
  // every render, which would invalidate React.memo on each button.
  const toggleTag = useCallback(async (category: CandidateTagCategory, tagKey: string) => {
    const isSelected = tags.some((t) => t.category === category && t.tagKey === tagKey);

    let newTags: { category: CandidateTagCategory; tagKey: string; customText?: string }[];

    if (isSelected) {
      // Remove tag
      newTags = tags
        .filter((t) => !(t.category === category && t.tagKey === tagKey))
        .map((t) => ({
          category: t.category,
          tagKey: t.tagKey,
          customText: t.customText || undefined,
        }));
    } else {
      // Add tag
      const customText =
        tagKey === PositiveTagKey.OTHER && category === CandidateTagCategory.POSITIVE
          ? positiveCustomText
          : tagKey === ConcernTagKey.OTHER && category === CandidateTagCategory.CONCERN
            ? concernCustomText
            : undefined;

      newTags = [
        ...tags.map((t) => ({
          category: t.category,
          tagKey: t.tagKey,
          customText: t.customText || undefined,
        })),
        { category, tagKey, customText },
      ];
    }

    await saveTags(newTags);
  }, [tags, positiveCustomText, concernCustomText, saveTags]);

  const handleSaveOtherCustomText = async (category: CandidateTagCategory) => {
    const customText =
      category === CandidateTagCategory.POSITIVE
        ? positiveCustomText
        : concernCustomText;
    const otherKey =
      category === CandidateTagCategory.POSITIVE
        ? PositiveTagKey.OTHER
        : ConcernTagKey.OTHER;

    if (!customText.trim()) return;

    // Update existing tags: update the "other" tag with new custom text
    const newTags = tags.map((t) => ({
      category: t.category,
      tagKey: t.tagKey,
      customText:
        t.category === category && t.tagKey === otherKey
          ? customText.trim()
          : t.customText || undefined,
    }));

    // If "other" tag doesn't exist yet, add it
    if (!isTagSelected(category, otherKey)) {
      newTags.push({
        category,
        tagKey: otherKey,
        customText: customText.trim(),
      });
    }

    await saveTags(newTags);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Beoordeling ophalen..." color="success" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Rating Section */}
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-semibold text-neutral-700">Score</h4>
        <div className="flex items-center gap-3">
          <StarRating value={rating} onChange={handleRatingChange} size={24} />
          {rating !== null && (
            <span className="text-sm text-neutral-500">
              {RATING_LABELS[rating]}
            </span>
          )}
          {isSavingRating && (
            <Spinner size="sm" color="success" />
          )}
        </div>
      </div>

      {/* Positive Tags */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <ThumbsUp size={16} weight="duotone" className="text-green-600" />
          <h4 className="text-sm font-semibold text-neutral-700">Pluspunten</h4>
          {isSavingTags && <Spinner size="sm" color="success" />}
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_POSITIVE_TAGS.filter((t) => t.key !== PositiveTagKey.OTHER).map(
            (tag) => (
              <button
                key={tag.key}
                onClick={() =>
                  toggleTag(CandidateTagCategory.POSITIVE, tag.key)
                }
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  isTagSelected(CandidateTagCategory.POSITIVE, tag.key)
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-neutral-200 text-neutral-600 hover:border-green-300 hover:bg-green-50/50"
                }`}
              >
                {tag.label}
              </button>
            )
          )}
        </div>
        {/* "Andere" with custom text */}
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              toggleTag(CandidateTagCategory.POSITIVE, PositiveTagKey.OTHER)
            }
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              isTagSelected(CandidateTagCategory.POSITIVE, PositiveTagKey.OTHER)
                ? "border-green-500 bg-green-50 text-green-700"
                : "border-neutral-200 text-neutral-600 hover:border-green-300 hover:bg-green-50/50"
            }`}
          >
            <span className="flex items-center gap-1">
              <Plus size={12} />
              Andere
            </span>
          </button>
          {isTagSelected(CandidateTagCategory.POSITIVE, PositiveTagKey.OTHER) && (
            <div className="flex flex-1 items-center gap-2">
              <input
                type="text"
                value={positiveCustomText}
                onChange={(e) => setPositiveCustomText(e.target.value)}
                placeholder="Beschrijf..."
                className="flex-1 rounded-md border border-neutral-200 px-2.5 py-1 text-sm outline-none transition-colors focus:border-moss-primary"
              />
              <ButtonNew
                variant="secondaryMoss"
                label="Opslaan"
                onClick={() =>
                  handleSaveOtherCustomText(CandidateTagCategory.POSITIVE)
                }
                isDisabled={!positiveCustomText.trim()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Concern Tags */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Warning size={16} weight="duotone" className="text-red-500" />
          <h4 className="text-sm font-semibold text-neutral-700">
            Aandachtspunten
          </h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_CONCERN_TAGS.filter((t) => t.key !== ConcernTagKey.OTHER).map(
            (tag) => (
              <button
                key={tag.key}
                onClick={() =>
                  toggleTag(CandidateTagCategory.CONCERN, tag.key)
                }
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  isTagSelected(CandidateTagCategory.CONCERN, tag.key)
                    ? "border-red-400 bg-red-50 text-red-600"
                    : "border-neutral-200 text-neutral-600 hover:border-red-300 hover:bg-red-50/50"
                }`}
              >
                {tag.label}
              </button>
            )
          )}
        </div>
        {/* "Andere" with custom text */}
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              toggleTag(CandidateTagCategory.CONCERN, ConcernTagKey.OTHER)
            }
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              isTagSelected(CandidateTagCategory.CONCERN, ConcernTagKey.OTHER)
                ? "border-red-400 bg-red-50 text-red-600"
                : "border-neutral-200 text-neutral-600 hover:border-red-300 hover:bg-red-50/50"
            }`}
          >
            <span className="flex items-center gap-1">
              <Plus size={12} />
              Andere
            </span>
          </button>
          {isTagSelected(CandidateTagCategory.CONCERN, ConcernTagKey.OTHER) && (
            <div className="flex flex-1 items-center gap-2">
              <input
                type="text"
                value={concernCustomText}
                onChange={(e) => setConcernCustomText(e.target.value)}
                placeholder="Beschrijf..."
                className="flex-1 rounded-md border border-neutral-200 px-2.5 py-1 text-sm outline-none transition-colors focus:border-moss-primary"
              />
              <ButtonNew
                variant="secondaryMoss"
                label="Opslaan"
                onClick={() =>
                  handleSaveOtherCustomText(CandidateTagCategory.CONCERN)
                }
                isDisabled={!concernCustomText.trim()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentTab;
